import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Logo from './Logo';

const LLM_URL = import.meta.env.VITE_LLM_URL || 'https://llm.etherworks.cn';
const CLOUD_URL = import.meta.env.VITE_CLOUD_URL || 'https://cloud.etherworks.cn';
const FINETUNE_URL = import.meta.env.VITE_FINETUNE_URL || 'https://ft.etherworks.cn';
const PRODUCT_NAME = import.meta.env.VITE_PRODUCT_NAME || 'Etherworks';

interface FooterLink {
  label: string;
  to?: string;
  href?: string;
  external?: boolean;
}

interface FooterColumn {
  title: string;
  links: FooterLink[];
}

export default function SiteFooter() {
  const { t } = useTranslation('landing');
  const year = new Date().getFullYear();

  const columns: FooterColumn[] = [
    {
      title: t('footer.product'),
      links: [
        { label: t('footer.reverse_model'), to: '/products/reverse-model' },
        { label: t('footer.fine_tuning'), href: FINETUNE_URL, external: true },
        { label: t('footer.gpu_cloud'), to: '/products/gpu-cloud' },
        { label: t('footer.pricing'), to: '/plans' },
      ],
    },
    {
      title: t('footer.use_cases'),
      links: [
        { label: t('footer.malware_analysis'), to: '/products/malware-analysis' },
        { label: t('footer.vuln_mining'), to: '/products/vulnerability-mining' },
      ],
    },
    {
      title: t('footer.resources'),
      links: [
        { label: t('footer.documentation'), href: `${LLM_URL}/docs`, external: true },
        { label: t('footer.cloud_console'), href: `${CLOUD_URL}/console`, external: true },
      ],
    },
    {
      title: t('footer.legal'),
      links: [
        { label: t('footer.terms'), to: '/terms' },
      ],
    },
  ];

  return (
    <footer className="border-t border-border bg-card/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8">
          {/* Brand column */}
          <div className="col-span-2 md:col-span-2">
            <Link to="/" className="inline-flex items-center gap-2 text-foreground hover:text-primary transition-colors">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 text-primary">
                <Logo size="w-5 h-5" />
              </div>
              <span className="text-base font-semibold tracking-tight">{PRODUCT_NAME}</span>
            </Link>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed max-w-xs">
              {t('footer.tagline')}
            </p>
          </div>

          {/* Link columns */}
          {columns.map((col, idx) => (
            <div key={idx}>
              <h4 className="text-sm font-semibold text-foreground mb-4">{col.title}</h4>
              <ul className="space-y-2.5">
                {col.links.map((link, lIdx) => (
                  <li key={lIdx}>
                    {link.to ? (
                      <Link
                        to={link.to}
                        className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {link.label}
                      </Link>
                    ) : (
                      <a
                        href={link.href}
                        target={link.external ? '_blank' : undefined}
                        rel={link.external ? 'noopener noreferrer' : undefined}
                        className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {link.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 pt-8 border-t border-border flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            {t('footer.copyright', { year, name: PRODUCT_NAME })}
          </p>
        </div>
      </div>
    </footer>
  );
}
